import { useState, useEffect, useCallback } from "react";
import { getDashboardData } from "../services/api/dashboard.service";
import { KycProfile } from "../types/dashboard";
import { useAuth } from "../contexts/AuthContext";

const MAX_RETRIES = 10; // Maximum number of retry attempts
const POLLING_INTERVAL = 3000; // 3 seconds between each attempt

/**
 * Custom hook to manage and provide dashboard data.
 *
 * @returns An object containing dashboard data, loading state, and any error encountered.
 */
export const useDashboardData = () => {
  const { authKey } = useAuth();
  const [data, setData] = useState<KycProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingCount, setPollingCount] = useState(0);
  const [pollingLoading, setPollingLoading] = useState(false);
  const [pollingError, setPollingError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!authKey) {
      setData(null);
      return null;
    }

    try {
      setLoading(true);
      const dashboardData = await getDashboardData();
      setData(dashboardData);
      return dashboardData;
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to fetch dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [authKey]);

  const startPolling = useCallback(async () => {
    setLoading(true);
    setPollingCount(0);
    setPollingError(null);
    setPollingLoading(true);

    const pollData = async () => {
      try {
        const result = await fetchData();
        console.log("successfully get it from polling");
        console.log(result);
        if (result) {
          setData(result);
          setLoading(false);
          setError(null);
          setPollingLoading(false);

          return true; // Successfully got data
        }
        return false;
      } catch (err) {
        if (pollingCount >= MAX_RETRIES) {
          setError(
            "Verification is taking longer than expected. Please contact support if this persists."
          );
          setLoading(false);
          setPollingLoading(false);

          return true; // Stop polling on max retries
        }
        return false;
      }
    };

    const poll = async () => {
      const shouldStop = await pollData();

      if (!shouldStop && pollingCount < MAX_RETRIES) {
        setPollingCount((prev) => prev + 1);
        setTimeout(poll, POLLING_INTERVAL);
      }
    };

    poll();
  }, [fetchData, pollingCount]);

  useEffect(() => {
    // Initial fetch on mount
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    startPolling,
    pollingCount,
    pollingLoading,
    pollingError,
  };
};
